/**
 * DirectReports
 *
 * Protected, chrome-less deep-link page:
 *   /#/infralytiqs/direct-reports?server=<CH server name>
 *                                &reportsuites=<csv suite names>
 *                                &theme=<PROFESSIONAL|DEFAULT>
 *
 * Behaviour:
 *   - Authenticated route. If the SSO flow lands here with `?ssoToken=...`, the
 *     token is stored and the Redux session is restored (same bootstrap as the
 *     Login page). If still unauthenticated afterwards, redirects to login.
 *   - Resolves the ClickHouse server (by name) and the report suites (by name)
 *     from the URL, restricted to what the logged-in user is permitted to see
 *     (i.e. what `/il/settings/module-configs` returns for them).
 *   - Renders a toolbar (ClickHouse Server + Report Suite dropdowns + time
 *     range) and the selected suite's panels via SuiteReportsView, reusing the
 *     existing Infralytiqs report panel components.
 *   - Applies a per-page theme override (PROFESSIONAL / DEFAULT) and hides the
 *     top nav while keeping the sidebar collapsed.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createTheme } from '@mui/material/styles';
import {
  Alert, Backdrop, Box, Button, CircularProgress, CssBaseline, FormControl,
  IconButton, InputLabel, MenuItem, Select, ThemeProvider, Tooltip, Typography,
  useTheme,
} from '@mui/material';
import {
  AccessTime, ChevronLeft, ChevronRight, PowerOff, Refresh, ZoomIn, ZoomOut,
} from '@mui/icons-material';
import { themeSettings, THEME_NAME_DEFAULT, THEME_NAME_PROFESSIONAL } from 'theme';
import { useResolvedThemeMode } from 'hooks/useResolvedThemeMode';
import { API_BASE_URL, THEME_AUTOMATIC, THEME_DARK, THEME_LIGHT } from 'envConfig';
import apiClient, { SESSION_EXPIRED_EVENT } from 'utils/ApiClient';
import { LocalStorageAuth } from 'utils/LocalStorageAuth';
import { RootState, AppDispatch } from 'state/store';
import { restoreReduxSession } from 'state/authentication/authenticationSlice';
import { useLoginPath } from 'hooks/useLoginPath';
import { TimeRangeDef } from '../Settings/reportDsl';
import { useReportsTimeRange } from '../Reports/hooks/useReportsTimeRange';
import { TimePickerPopover } from '../Logs/components/TimePickerPopover';
import { REPORTS_QUICK_RANGES, REPORTS_CUSTOM_TIME_MAX_MS } from '../Reports/reportsTimeRange';
import {
  reportsToolbarSurfaceSx,
  reportsTimeRangeIconButtonSx,
  reportsTimeRangePickerButtonSx,
  reportsToolbarIconButtonSx,
  reportsDrilldownBackButtonSx,
} from '../Reports/reportsToolbarSurface';
import { resolveReportsSuiteId, resolveSuiteIdByName } from '../reportsDefaultServer';
import { DirectReportsLayout } from './DirectReportsLayout';
import { SuiteReportsView } from './SuiteReportsView';

interface ModuleConfig {
  _id: string;
  moduleType: string;
  name: string;
  tenantId?: string;
  siteId?: string;
  defaultReportSuiteId?: string;
  defaultTimeRange?: string;
  reportSuites?: Array<{ _id: string; name: string; description?: string }>;
}

interface SuiteOption {
  _id: string;
  name: string;
  description?: string;
}

const msToChDateTime = (ms: number): string =>
  new Date(ms).toISOString().replace('T', ' ').replace('Z', '').slice(0, 23);

// ─────────────────────────────────────────────────────────────────────────────
// Authenticated content: configs resolution + toolbar + suite rendering.
// Mounted only once the user is authenticated, so hook order stays stable.
// ─────────────────────────────────────────────────────────────────────────────
const DirectReportsContent: React.FC = () => {
  const theme = useTheme();
  const apiBaseUrl = useSelector(
    (state: RootState) => state.clientConfig.data?.apiBaseUrl || API_BASE_URL,
  );
  const [searchParams] = useSearchParams();
  const serverParam = (searchParams.get('server') || '').trim();
  const suitesParam = useMemo(
    () => (searchParams.get('reportsuites') || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    [searchParams],
  );
  const suitesParamKey = suitesParam.join('||');
  const defaultSuiteParam = (searchParams.get('defaultsuite') || '').trim();

  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [resolvedConfig, setResolvedConfig] = useState<ModuleConfig | null>(null);
  const [allowedSuites, setAllowedSuites] = useState<SuiteOption[]>([]);
  const [selectedSuiteId, setSelectedSuiteId] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Session-expiry handling: panels fetch independently and surface a per-panel
  // "Forbidden Access, No user found for the session token" error once the
  // session lapses. ApiClient broadcasts SESSION_EXPIRED_EVENT on that 403, and
  // we promote it to a single full-screen, theme-matched recovery overlay.
  const [sessionExpired, setSessionExpired] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const onSessionExpired = () => setSessionExpired(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  // Re-trigger SSO by sending the user back through the original shared link.
  // We reconstruct the SSO "initiate" URL from the provider captured in the deep
  // link (`sso` param) and use the current deep link (ssoToken already stripped
  // on bootstrap) as redirect_uri. With no provider we just reload so the normal
  // auth flow runs.
  const handleRefreshSession = () => {
    setRedirecting(true);
    const provider = (searchParams.get('sso') || '').trim();
    if (provider) {
      const base = (apiBaseUrl || '').replace(/\/+$/, '');
      const redirectUri = window.location.href;
      window.location.href = `${base}/auth/sso/${encodeURIComponent(provider)}/initiate?redirect_uri=${encodeURIComponent(redirectUri)}`;
      return;
    }
    window.location.reload();
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingConfigs(true);
      setConfigError(null);
      setResolvedConfig(null);
      setAllowedSuites([]);
      setSelectedSuiteId('');
      try {
        const res: any = await apiClient.get('/il/settings/module-configs');
        if (cancelled) return;
        const list: ModuleConfig[] = Array.isArray(res?.data)
          ? res.data
          : (res?.data?.moduleConfigurations ?? []);
        const clickhouse = list.filter((c) => c.moduleType === 'ClickHouse');

        if (!serverParam) {
          setConfigError('No ClickHouse server was supplied in the URL (missing "server" parameter).');
          return;
        }
        const cfg = clickhouse.find((c) => (c.name || '').toLowerCase() === serverParam.toLowerCase());
        if (!cfg) {
          setConfigError(
            `You do not have permission to view the ClickHouse server "${serverParam}", or it does not exist.`,
          );
          return;
        }

        const suites = cfg.reportSuites ?? [];
        const wanted: SuiteOption[] = suitesParam.length > 0
          ? suites.filter((s) => suitesParam.some((w) => w.toLowerCase() === (s.name || '').toLowerCase()))
          : suites;

        if (wanted.length === 0) {
          setConfigError(
            suitesParam.length > 0
              ? `You do not have permission to view the requested report suite(s) on server "${serverParam}".`
              : `The ClickHouse server "${serverParam}" has no report suites.`,
          );
          return;
        }

        const namedDefaultId = resolveSuiteIdByName(wanted, defaultSuiteParam);
        const resolvedDefault = namedDefaultId || resolveReportsSuiteId(
          { _id: cfg._id, reportSuites: wanted, defaultReportSuiteId: cfg.defaultReportSuiteId },
        );
        setResolvedConfig(cfg);
        setAllowedSuites(wanted);
        setSelectedSuiteId(
          resolvedDefault && wanted.some((s) => s._id === resolvedDefault)
            ? resolvedDefault
            : wanted[0]._id,
        );
      } catch (err: any) {
        if (!cancelled) setConfigError(err?.message || 'Failed to load configurations');
      } finally {
        if (!cancelled) setLoadingConfigs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [serverParam, suitesParamKey, defaultSuiteParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const scopeKey = resolvedConfig && selectedSuiteId
    ? `${resolvedConfig._id}::${selectedSuiteId}`
    : '';
  const timeRange = useReportsTimeRange({
    scopeKey,
    defaultTimeRangeLabel: resolvedConfig?.defaultTimeRange || '',
  });
  const pickerDates = timeRange.getPickerInitialDates();

  const timeRangeOverride: TimeRangeDef = timeRange.activePreset
    ? { preset: timeRange.activePreset }
    : (() => {
      const { start, end } = timeRange.getTimeRangeMs();
      return { start: msToChDateTime(start), end: msToChDateTime(end) };
    })();

  if (loadingConfigs) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (configError || !resolvedConfig) {
    return (
      <Box sx={{ pt: 2 }}>
        <Alert severity="warning">{configError || 'Unable to load reports.'}</Alert>
      </Box>
    );
  }

  return (
    <>
    <Box sx={{ width: '100%', mb: 3 }}>
      {/* Toolbar: server + suite dropdowns + time range. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1, md: 2 },
          alignItems: 'center',
          mb: 3,
          p: { xs: 1, md: 1.5 },
          ...reportsToolbarSurfaceSx,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 200, flex: { xs: 1, md: '0 1 220px' } }}>
          <InputLabel>ClickHouse Server</InputLabel>
          <Select label="ClickHouse Server" value={resolvedConfig._id} disabled>
            <MenuItem value={resolvedConfig._id}>{resolvedConfig.name}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
          <InputLabel>Report Suite</InputLabel>
          <Select
            label="Report Suite"
            value={allowedSuites.some((s) => s._id === selectedSuiteId) ? selectedSuiteId : ''}
            onChange={(e) => setSelectedSuiteId(e.target.value as string)}
            disabled={allowedSuites.length === 0}
          >
            {allowedSuites.map((s) => (
              <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="Zoom in time range">
            <span>
              <IconButton
                size="small"
                onClick={timeRange.handleZoomIn}
                disabled={timeRange.timeRangeLabel === REPORTS_QUICK_RANGES[0].label}
                sx={reportsTimeRangeIconButtonSx(theme)}
              >
                <ZoomIn fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Shift time range back">
            <IconButton size="small" onClick={() => timeRange.shiftTime('back')} sx={reportsTimeRangeIconButtonSx(theme)}>
              <ChevronLeft fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            color={theme.palette.mode === 'light' ? undefined : 'secondary'}
            onClick={timeRange.handleTimePickerOpen}
            startIcon={<AccessTime sx={{ fontSize: 16 }} />}
            sx={reportsTimeRangePickerButtonSx(theme)}
          >
            {timeRange.timeRangeLabel}
          </Button>
          <Tooltip title="Shift time range forward">
            <IconButton size="small" onClick={() => timeRange.shiftTime('forward')} sx={reportsTimeRangeIconButtonSx(theme)}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom out time range">
            <IconButton size="small" onClick={timeRange.handleZoomOut} sx={reportsTimeRangeIconButtonSx(theme)}>
              <ZoomOut fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Tooltip title="Refresh all panels">
          <IconButton
            onClick={() => setRefreshKey((k) => k + 1)}
            sx={reportsToolbarIconButtonSx(theme)}
            aria-label="Refresh all panels"
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <TimePickerPopover
        anchorEl={timeRange.anchorEl}
        onClose={() => timeRange.setAnchorEl(null)}
        timeRangeLabel={timeRange.timeRangeLabel}
        onQuickRangeSelect={(r) => {
          const full = REPORTS_QUICK_RANGES.find((q) => q.label === r.label);
          if (full) timeRange.handleQuickRangeSelect(full);
        }}
        onApplyCustomRange={timeRange.handleApplyCustomRange}
        initialFrom={pickerDates.from}
        initialTo={pickerDates.to}
        tzName={timeRange.tzName}
        tzString={timeRange.tzString}
        maxCustomRangeMs={REPORTS_CUSTOM_TIME_MAX_MS}
        quickRanges={REPORTS_QUICK_RANGES}
      />

      {selectedSuiteId ? (
        <SuiteReportsView
          configId={resolvedConfig._id}
          tenantId={resolvedConfig.tenantId || ''}
          siteId={resolvedConfig.siteId || ''}
          suiteId={selectedSuiteId}
          timeRangeOverride={timeRangeOverride}
          refreshKey={refreshKey}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">Select a report suite to begin.</Typography>
      )}
    </Box>

      <Backdrop
        open={sessionExpired}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: (t) => t.zIndex.modal + 10,
          flexDirection: 'column',
          gap: 2,
          backgroundColor: theme.palette.mode === 'light'
            ? 'rgba(247, 248, 250, 0.92)'
            : 'rgba(10, 14, 20, 0.92)',
          backdropFilter: 'blur(3px)',
          color: 'text.primary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2,
            px: 4,
            py: 5,
            maxWidth: 460,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            boxShadow: 6,
          }}
        >
          <PowerOff sx={{ fontSize: 64, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Session expired
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please click the Refresh button to validate the session again.
          </Typography>
          <Tooltip title="Validate session again">
            <span>
              <IconButton
                onClick={handleRefreshSession}
                disabled={redirecting}
                aria-label="Refresh and validate session"
                size="large"
                sx={reportsDrilldownBackButtonSx(theme)}
              >
                {redirecting ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Backdrop>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page wrapper: theme override + SSO bootstrap + auth gate + chrome-less layout.
// ─────────────────────────────────────────────────────────────────────────────
export const DirectReports: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const mode = useSelector((state: RootState) => state.global.mode);
  const user = useSelector((state: RootState) => state.authentication.user);
  const loginPath = useLoginPath();

  // Per-page theme override (independent of the domain-wide clientConfig theme).
  const mappedThemeName = (searchParams.get('theme') || '').toUpperCase() === 'PROFESSIONAL'
    ? THEME_NAME_PROFESSIONAL
    : THEME_NAME_DEFAULT;
  const themeTypeParam = (searchParams.get('themetype') || '').toUpperCase();
  const themePreference = themeTypeParam === 'LIGHT'
    ? THEME_LIGHT
    : themeTypeParam === 'DARK'
      ? THEME_DARK
      : themeTypeParam === 'AUTOMATIC'
        ? THEME_AUTOMATIC
        : (mode || THEME_DARK);
  const themeMode = useResolvedThemeMode(themePreference);
  const theme = useMemo(
    () => createTheme(themeSettings(themeMode, mappedThemeName)),
    [themeMode, mappedThemeName],
  );

  // SSO bootstrap: the deep-link can arrive with ?ssoToken=... appended by the
  // server after a successful SSO round trip. Store it and restore the session
  // (same flow as the Login page), then strip it from the URL.
  const ssoToken = searchParams.get('ssoToken');
  const [ssoRestoring, setSsoRestoring] = useState<boolean>(Boolean(ssoToken));

  useEffect(() => {
    if (!ssoToken) return;
    LocalStorageAuth.setToken(ssoToken);
    const next = new URLSearchParams(searchParams);
    next.delete('ssoToken');
    next.delete('ssoError');
    next.delete('ssoEmail');
    setSearchParams(next, { replace: true });
    dispatch(restoreReduxSession())
      .unwrap()
      // Re-assert the token after restore resolves: a concurrent request (e.g. a
      // stale-session restore racing on app bootstrap) may have triggered a
      // storage clear in-flight, which would otherwise leave this deep-link tab
      // authenticated in Redux but tokenless for subsequent API calls.
      .then(() => { LocalStorageAuth.setToken(ssoToken); })
      .catch(() => { /* fall through to the auth gate below */ })
      .finally(() => setSsoRestoring(false));
    // Run once on mount for the inbound token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {ssoRestoring ? (
        <Backdrop
          sx={(t) => ({ color: t.palette.secondary.main, zIndex: t.zIndex.drawer + 1 })}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : user == null ? (
        <Navigate to={loginPath} replace />
      ) : (
        <DirectReportsLayout>
          <DirectReportsContent />
        </DirectReportsLayout>
      )}
    </ThemeProvider>
  );
};

export default DirectReports;
